import { inject, Injectable, Type } from "@angular/core";
import { ModalController, ModalOptions } from "@ionic/angular/standalone";
import { Subject } from "rxjs";

import { BaseModalComponent } from "../base/base-modal.component";
import { BaseService } from "../base/base.service";

export enum ModalEventType {
    Open = "open",
    Close = "close"
}
export class ModalEvent {
    public type: ModalEventType;
    public modalElement: HTMLIonModalElement;
    public modalName: string;
    public constructor(type: ModalEventType, modalElement: HTMLIonModalElement, modalName: string) {
        this.type = type;
        this.modalElement = modalElement;
        this.modalName = modalName;
    }
}

interface IModalElement {
    id: number;
    ionModal: HTMLIonModalElement;
}

export interface OpenModalOptions extends Partial<ModalOptions> {
    showCloseButton?: boolean;
}

@Injectable({
    providedIn: "root"
})
export class ModalService extends BaseService {
    public readonly modalEvents = new Subject<ModalEvent>();

    private appModals = new Array<IModalElement>();
    private currentId = 0;
    private isTimeoutActive = false;

    private modalController = inject(ModalController);

    private readonly modalCloseButtonClass = "mp-modal-close-button";
    private readonly modalCloseButtonStyleId = "mp-modal-close-button-style";

    public isTimeoutModalActive(): boolean {
        return this.isTimeoutActive;
    }

    public setTimeoutModalActive(isTimeoutActive: boolean): void {
        this.isTimeoutActive = isTimeoutActive;
    }

    public async openModal(modalComponent: Type<BaseModalComponent>, data?: { [key: string]: unknown }, overrideCreationProps?: OpenModalOptions, isManaged = true): Promise<HTMLIonModalElement> {
        const modal = await this.createModal(modalComponent, data, overrideCreationProps);
        const showCloseButton = overrideCreationProps?.showCloseButton ?? true;
        if (isManaged) {
            const createdModal = this.addOpenModal(modal);
            modal.onWillDismiss().then(() => {
                const modalIx = this.appModals.map(modalElement => modalElement.id).indexOf(createdModal.id);
                this.appModals.splice(modalIx, 1);
                this.log.debug("Modal dismissed. Current active list:", this.appModals);
            });
        }
        this.log.debug(`Modal of type ${modalComponent.name} created`);
        this.modalEvents.next(new ModalEvent(ModalEventType.Open, modal, modalComponent.name));
        await modal.present();
        if (showCloseButton) {
            this.attachCloseButton(modal);
        }
        modal.onDidDismiss().then(() => {
            this.modalEvents.next(new ModalEvent(ModalEventType.Close, modal, modalComponent.name));
        });
        this.log.debug(`Modal of type ${modalComponent.name} presented`);
        return modal;
    }

    public async openLandscapeModal<RT>(
        modalComponent: Type<BaseModalComponent>,
        data?: { [key: string]: unknown },
        overrideCreationProps?: OpenModalOptions,
        isManaged = true
    ): Promise<RT | undefined> {
        const modal = await this.openModal(modalComponent, data, { ...overrideCreationProps, cssClass: "mp-ion-modal mp-landscape-modal", showBackdrop: false }, isManaged);
        const { data: result } = await modal.onWillDismiss<RT>();
        return result;
    }

    public async closeAllModals(): Promise<void> {
        if (this.appModals && this.appModals.length > 0) {
            await Promise.all(this.appModals.map(modal => (modal && modal.ionModal ? modal.ionModal.dismiss().catch(err => this.log.warn("Error on dismiss modal", err)) : Promise.resolve())));
            this.resetOpenModals();
        }
    }

    public isSomeModalOpen(): boolean {
        return this.appModals && this.appModals.length > 0;
    }

    public addOpenModal(toSetModal: HTMLIonModalElement): IModalElement {
        const newModal = {
            id: this.currentId,
            ionModal: toSetModal
        } as IModalElement;

        this.appModals.push(newModal);

        this.currentId++;
        return newModal;
    }

    public async applyModalLayout(isLandscape: boolean, otherClasses?: { add?: string; remove?: string }): Promise<void> {
        try {
            const ionModal = await this.modalController.getTop();
            if (!ionModal) {
                return;
            }
            if (isLandscape) {
                ionModal.classList.add("mp-landscape-modal");
            } else {
                ionModal.classList.remove("mp-landscape-modal");
            }
            if (otherClasses?.add) {
                ionModal.classList.add(otherClasses.add);
            }
            if (otherClasses?.remove) {
                ionModal.classList.remove(otherClasses.remove);
            }
        } catch (err) {
            this.log.warn("Unable to update modal layout", err);
        }
    }

    private async createModal(modalComponent: Type<BaseModalComponent>, data: { [key: string]: unknown }, overrideCreationProps?: OpenModalOptions): Promise<HTMLIonModalElement> {
        return this.modalController.create({
            component: modalComponent,
            componentProps: data,
            cssClass: "mp-ion-modal",
            backdropDismiss: true,
            ...this.getIonicModalOptions(overrideCreationProps)
        });
    }

    private getIonicModalOptions(overrideCreationProps?: OpenModalOptions): Partial<ModalOptions> {
        const modalOptions = { ...(overrideCreationProps ?? {}) };
        delete modalOptions.showCloseButton;
        return modalOptions;
    }

    private attachCloseButton(modal: HTMLIonModalElement): void {
        const modalShadowRoot = modal.shadowRoot;
        const modalContent = modalShadowRoot?.querySelector("[part='content']");
        if (!modalShadowRoot || !modalContent || modalContent.querySelector(`.${this.modalCloseButtonClass}`)) {
            return;
        }

        this.appendCloseButtonStyles(modalShadowRoot);

        const closeButton = document.createElement("button");
        closeButton.type = "button";
        closeButton.className = this.modalCloseButtonClass;
        closeButton.setAttribute("aria-label", "Close modal");
        closeButton.textContent = "×";
        closeButton.addEventListener("click", () => {
            modal.dismiss().catch(err => this.log.warn("Error on dismiss modal", err));
        });

        modalContent.appendChild(closeButton);
    }

    private appendCloseButtonStyles(modalShadowRoot: ShadowRoot): void {
        if (modalShadowRoot.getElementById(this.modalCloseButtonStyleId)) {
            return;
        }

        const style = document.createElement("style");
        style.id = this.modalCloseButtonStyleId;
        style.textContent = `
            .${this.modalCloseButtonClass} {
                position: absolute;
                top: 12px;
                right: 12px;
                z-index: 2;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                border: 0;
                border-radius: 999px;
                padding: 0;
                background: rgba(255, 255, 255, 0.96);
                color: var(--ion-color-dark, #1f2937);
                cursor: pointer;
                font-size: 24px;
                line-height: 1;
            }

            .${this.modalCloseButtonClass}:focus-visible {
                outline: 2px solid var(--ion-color-primary, #3880ff);
                outline-offset: 2px;
            }
        `;

        modalShadowRoot.appendChild(style);
    }

    private resetOpenModals(): void {
        this.appModals = new Array<IModalElement>();
    }
}
